import { Fragment, type ReactNode } from "react";
import { BookOpen, CircleAlert, CircleCheck, Code, Container, ExternalLink, GitBranch, Globe, KeyRound, Workflow, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { relative } from "../../lib/time";
import { fill, safeHref } from "../../lib/fill";
import { Button } from "../primitives/button";
import { StatusRing } from "../data/cards";

export interface ConnectedSource {
  /** Stable key (the source's id). Two sources may share a name; without an id the list keys by position. */
  id?: string;
  kind: "ci" | "api" | "git" | "url" | "registry";
  name: string;
  detail: string;
  lastAt: Date | null;
  state: "working" | "failing" | "never" | "paused";
  /** For failing sources: the reason and the fix (SourceHealth.lastError). */
  error?: string | null;
}
const icon: Record<ConnectedSource["kind"], LucideIcon> = { ci: Workflow, api: KeyRound, git: GitBranch, url: Globe, registry: Container };

/** Maps the API's SourceHealth.type (13 §Source type) to the card's icon kind. */
export function sourceKind(type: string): ConnectedSource["kind"] {
  if (type === "cli" || type === "sbom") return "ci";
  if (["github", "gitlab", "bitbucket", "azure", "git"].includes(type)) return "git";
  if (type === "registry") return "registry";
  if (type === "url" || type === "page") return "url";
  return "api";
}

/** Every text of the card, for translation (REQUESTS #103 d). English defaults. */
export interface AutomationCardLabels {
  title: string;
  intro: string;
  /**
   * The counts sentence; gets the counts as styled numbers (automated is undefined when unknown) and, third, the raw
   * numbers to pick the plural form (D18: `ui.automatic_imports_count_*`, `ui.manual_uploads_count_*`,
   * `ui.last_30_days_imports_uploads`, `ui.last_30_days_uploads`).
   */
  counts: (automated: ReactNode | undefined, manual: ReactNode, n: { automated?: number; manual: number }) => ReactNode;
  apiLink: string;
  docsLink: string;
  opensInNewTab: string;
  /** `{when}`: "2 hours ago". */
  lastPush: string;
  /** `{via}`: "GitHub Actions". */
  via: string;
  working: string;
  failing: string;
  paused: string;
  never: string;
}
function defaultCounts(automated: ReactNode | undefined, manual: ReactNode, n: { automated?: number; manual: number }): ReactNode {
  const uploads = (
    <>
      {manual} {n.manual === 1 ? "manual upload" : "manual uploads"}
    </>
  );
  if (automated === undefined) return fillNodes(LAST_30_UPLOADS, { uploads });
  const imports = (
    <>
      {automated} {n.automated === 1 ? "automatic import" : "automatic imports"}
    </>
  );
  return fillNodes(LAST_30_IMPORTS_UPLOADS, { imports, uploads });
}
// One message each (`ui.last_30_days_uploads`, `ui.last_30_days_imports_uploads`), not fragments around the counts.
const LAST_30_UPLOADS = "In the last 30 days: {uploads}.";
const LAST_30_IMPORTS_UPLOADS = "In the last 30 days: {imports} and {uploads}.";
/** `{name}` placeholders filled with nodes. */
type NodeValues = { [name: string]: ReactNode };
function fillNodes(template: string, values: NodeValues): ReactNode {
  return template.split(/\{(\w+)\}/).map((part, i) => (i % 2 === 1 ? <Fragment key={i}>{values[part]}</Fragment> : part));
}
export const automationCardLabels: AutomationCardLabels = {
  title: "Automation",
  intro: "New releases reach Brevalt on their own.",
  counts: defaultCounts,
  apiLink: "API & integrations",
  docsLink: "API docs",
  opensInNewTab: "(opens in a new tab)",
  lastPush: "Last push {when}:",
  via: "via {via}",
  working: "Working",
  failing: "Last push failed",
  paused: "Paused",
  never: "Not used yet",
};

/**
 * Automation / connected-sources card (D0 principle): releases arrive by
 * CI/API, so nobody uploads by hand. Entry to "API & integrations" and, when
 * `docsHref` is given, the API docs (a new tab).
 */
export function AutomationCard({
  sources,
  lastPush,
  manualUploads30d,
  now,
  apiHref = "/integrations",
  docsHref,
  canManage = true,
  automatedImports30d,
  labels,
}: {
  sources: ConnectedSource[];
  lastPush?: { at: Date; what: string; via: string };
  manualUploads30d: number;
  now: Date;
  apiHref?: string;
  /** The API docs (opens in a new tab); no link without it. `https:` or a same-origin path only. */
  docsHref?: string;
  /** integration.manage: show the "API & integrations" entry. */
  canManage?: boolean;
  automatedImports30d?: number;
  labels?: Partial<AutomationCardLabels>;
}) {
  const l = { ...automationCardLabels, ...labels };
  // Only https: or same-origin paths; anything else hides the link.
  const api = safeHref(apiHref);
  const docs = safeHref(docsHref);
  const num = (n: number) => <span className="tnum font-medium text-text">{n}</span>;
  return (
    <section aria-labelledby="bv-auto-h" className="card min-w-0 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="bv-auto-h" className="heading text-lg">
            {l.title}
          </h2>
          <p className="text-sm text-text-muted">
            {l.intro} {l.counts(automatedImports30d !== undefined ? num(automatedImports30d) : undefined, num(manualUploads30d), { automated: automatedImports30d, manual: manualUploads30d })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && api && (
            <Button size="s" icon={Code} asChild>
              <a href={api}>{l.apiLink}</a>
            </Button>
          )}
          {docs && (
            <a href={docs} target="_blank" rel="noreferrer" className="inline-flex h-touch items-center gap-1.5 rounded-btn px-3 text-xs font-medium text-brand-600 hover:bg-surface-2 lg:h-8">
              <BookOpen aria-hidden className="size-4" /> {l.docsLink} <ExternalLink aria-hidden className="size-3" />
              <span className="sr-only">{l.opensInNewTab}</span>
            </a>
          )}
        </div>
      </div>
      {lastPush && (
        <p className="mt-4 rounded-card bg-brand-50 px-4 py-3 text-sm">
          <span className="font-semibold">{fill(l.lastPush, { when: relative(lastPush.at, now) })}</span> {lastPush.what} <span className="text-text-muted">{fill(l.via, { via: lastPush.via })}</span>
        </p>
      )}
      <ul className="mt-3 divide-y divide-border">
        {sources.map((s, i) => {
          const Icon = icon[s.kind];
          return (
            <li key={s.id ?? `${i}:${s.name}`} className="flex items-center gap-3 py-2.5">
              <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-full bg-surface-2 text-text-muted">
                <Icon className="size-4" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.name}</p>
                <p className="truncate text-2xs text-text-muted">{s.detail}</p>
                {s.state === "failing" && s.error && <p className="mt-1 text-2xs text-exploited">{s.error}</p>}
              </div>
              <span className="shrink-0 text-right text-2xs">
                {s.state === "working" ? (
                  <span className="flex items-center justify-end gap-1 font-semibold text-ok">
                    <CircleCheck aria-hidden className="size-3.5" /> {l.working}
                  </span>
                ) : s.state === "failing" ? (
                  <span className="flex items-center justify-end gap-1 font-semibold text-exploited">
                    <CircleAlert aria-hidden className="size-3.5" /> {l.failing}
                  </span>
                ) : s.state === "paused" ? (
                  <span className="text-text-muted">{l.paused}</span>
                ) : (
                  <span className="text-text-muted">{l.never}</span>
                )}
                {s.lastAt && <span className="tnum block text-text-muted">{relative(s.lastAt, now)}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Calm "nothing to do" state (D0 principle): the default when nothing needs the user. */
export function CalmState({ title = "All products monitored. No action needed.", children, action, compact }: { title?: string; children?: ReactNode; action?: ReactNode; compact?: boolean }) {
  return (
    <section aria-label={title} className={cn("card flex flex-col gap-4 sm:flex-row sm:items-center", compact ? "p-4" : "rounded-modal p-6 shadow-raised")}>
      <StatusRing severity="ok" size={compact ? 36 : 64} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className={cn("heading", compact ? "text-base" : "text-xl")}>{title}</p>
        {children && <div className="text-sm text-text-muted">{children}</div>}
      </div>
      {action}
    </section>
  );
}
